import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFarm } from '../contexts/FarmContext';
import { billingAPI, authAPI, reportsAPI } from '../services/api';
import { 
  ArrowUpTrayIcon, 
  DocumentArrowDownIcon, 
  EnvelopeIcon, 
  ChartBarIcon,
  UserCircleIcon,
  CreditCardIcon,
  BellIcon,
  ShieldCheckIcon,
  SparklesIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const Profile = () => {
  const { user, updateProfile } = useAuth();
  const { currentFarm } = useFarm();
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ firstName: '', lastName: '', phone: '', avatarUrl: '' });
  const [saving, setSaving] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  
  // Reports state
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);

  useEffect(() => {
    setForm({ firstName: user?.firstName || '', lastName: user?.lastName || '', phone: user?.phone || '', avatarUrl: user?.avatarUrl || '' });
    const load = async () => {
      try {
        const res = await billingAPI.getStatus();
        setStatus(res.data);
      } catch {}
    };
    load();
  }, [user]);
  
  // Load available months when farm changes
  useEffect(() => {
    const loadMonths = async () => {
      if (!currentFarm?.id) return;
      try {
        const res = await reportsAPI.getAvailableMonths(currentFarm.id);
        setAvailableMonths(res.data?.months || []);
        if (res.data?.months?.length > 0) {
          setSelectedMonth(res.data.months[0]);
        }
      } catch (e) {
        console.error('Failed to load available months:', e);
      }
    };
    loadMonths();
  }, [currentFarm?.id]);

  const handleDownloadReport = async () => {
    if (!currentFarm?.id || !selectedMonth) return;
    try {
      setReportLoading(true);
      const res = await reportsAPI.downloadMonthlyPdf(currentFarm.id, {
        year: selectedMonth.year,
        month: selectedMonth.month
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Omabra_Report_${selectedMonth.label.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded!');
    } catch (e) {
      console.error('Download report error:', e);
      const errorMsg = e.response?.data?.error || e.response?.data?.message || 'Failed to download report';
      toast.error(errorMsg);
    } finally {
      setReportLoading(false);
    }
  };

  const handleSendReport = async () => {
    if (!currentFarm?.id || !selectedMonth) return;
    try {
      setSendingReport(true);
      await reportsAPI.sendMonthlyReport(currentFarm.id, {
        year: selectedMonth.year,
        month: selectedMonth.month
      });
      toast.success('Report sent to your email!');
    } catch (e) {
      console.error('Send report error:', e);
      const errorMsg = e.response?.data?.error || e.response?.data?.message || 'Failed to send report';
      toast.error(errorMsg);
    } finally {
      setSendingReport(false);
    }
  };

  const onSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      let avatarUrl = form.avatarUrl;
      if (avatarFile) {
        const res = await authAPI.uploadAvatar(avatarFile);
        avatarUrl = res?.data?.url || form.avatarUrl;
      }
      await updateProfile({ ...form, avatarUrl });
      toast.success('Profile updated');
    } catch (e) {
      toast.error('Failed to update');
    } finally { setSaving(false); }
  };

  const sub = status?.subscription;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-green-600 via-emerald-600 to-teal-700 rounded-2xl p-6 sm:p-8 text-white">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="h-20 w-20 rounded-2xl bg-white/20 backdrop-blur overflow-hidden border-2 border-white/30 flex-shrink-0">
            {form.avatarUrl ? (
              <img src={form.avatarUrl} alt="avatar" className="w-full h-full object-cover"/>
            ) : (
              <div className="w-full h-full grid place-items-center">
                <UserCircleIcon className="h-12 w-12 text-white/70" />
              </div>
            )}
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">{user?.firstName || 'User'} {user?.lastName || ''}</h1>
            <p className="text-green-100">{user?.email}</p>
            <div className="flex items-center gap-2 mt-2">
              <span className="inline-flex items-center px-2.5 py-1 rounded-lg bg-white/20 backdrop-blur text-xs font-medium">
                <ShieldCheckIcon className="h-3 w-3 mr-1" />
                {user?.role || 'User'}
              </span>
              {sub?.status === 'active' && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-lg bg-green-400/30 text-xs font-medium">
                  <CheckCircleIcon className="h-3 w-3 mr-1" />
                  Active
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Account Card */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <UserCircleIcon className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Account Information</h2>
              <p className="text-sm text-gray-500">Update your personal details</p>
            </div>
          </div>
          <form onSubmit={onSave} className="p-6">
            <div className="flex items-center gap-4 mb-6 pb-6 border-b border-gray-100">
              <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden border border-gray-200 flex-shrink-0">
                {form.avatarUrl ? (
                  <img src={form.avatarUrl} alt="avatar" className="w-full h-full object-cover"/>
                ) : (
                  <div className="w-full h-full grid place-items-center">
                    <UserCircleIcon className="h-8 w-8 text-gray-400" />
                  </div>
                )}
              </div>
              <div>
                <label className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer text-sm font-medium">
                  <ArrowUpTrayIcon className="h-4 w-4"/>
                  Upload Photo
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => { 
                    const f = e.target.files?.[0]; 
                    if (f) setAvatarFile(f); 
                  }}/>
                </label>
                <p className="text-xs text-gray-500 mt-1">JPG, PNG. Max 2MB</p>
              </div>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">First Name</label>
                <input 
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all" 
                  placeholder="Enter first name" 
                  value={form.firstName} 
                  onChange={(e) => setForm(prev => ({...prev, firstName: e.target.value}))} 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Last Name</label>
                <input 
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all" 
                  placeholder="Enter last name" 
                  value={form.lastName} 
                  onChange={(e) => setForm(prev => ({...prev, lastName: e.target.value}))} 
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number</label>
                <input 
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all" 
                  placeholder="e.g., 0712345678" 
                  value={form.phone} 
                  onChange={(e) => setForm(prev => ({...prev, phone: e.target.value}))} 
                />
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
              <button 
                className="px-6 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:shadow-lg hover:shadow-green-500/25 transition-all font-medium disabled:opacity-50" 
                type="submit" 
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>

        {/* Subscription Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
            <div className="p-2 bg-purple-50 rounded-lg">
              <CreditCardIcon className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Subscription</h2>
              <p className="text-sm text-gray-500">Your plan details</p>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <span className="text-sm text-gray-600">Status</span>
                <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                  sub?.status === 'active' ? 'bg-green-100 text-green-700' : 
                  sub?.status === 'pending' ? 'bg-amber-100 text-amber-700' : 
                  'bg-gray-100 text-gray-700'
                }`}>
                  {sub?.status || 'Trial'}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <span className="text-sm text-gray-600">Role</span>
                <span className="text-sm font-medium text-gray-900 capitalize">{user?.role || 'User'}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <span className="text-sm text-gray-600">Expires</span>
                <span className="text-sm font-medium text-gray-900">
                  {sub?.endsAt ? new Date(sub.endsAt).toLocaleDateString() : '—'}
                </span>
              </div>
            </div>
            <a 
              href="/billing" 
              className="mt-6 w-full inline-flex items-center justify-center px-4 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl hover:shadow-lg hover:shadow-purple-500/25 transition-all font-medium text-sm"
            >
              <CreditCardIcon className="h-4 w-4 mr-2" />
              Manage Billing
            </a>
          </div>
        </div>

        {/* Monthly Reports Section */}
        {currentFarm && (
          <div className="lg:col-span-3 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <ChartBarIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">Monthly Farm Reports</h2>
                <p className="text-sm text-gray-500">Download or email detailed farm performance reports</p>
              </div>
            </div>
            <div className="p-6">
              {availableMonths.length === 0 ? (
                <div className="text-center py-8">
                  <ChartBarIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No reports available yet</p>
                  <p className="text-sm text-gray-400 mt-1">Reports are generated after your first full month of data.</p>
                </div>
              ) : (
                <div className="flex flex-col lg:flex-row lg:items-end gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Select Month</label>
                    <select
                      className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      value={selectedMonth ? `${selectedMonth.year}-${selectedMonth.month}` : ''}
                      onChange={(e) => {
                        const [year, month] = e.target.value.split('-');
                        const found = availableMonths.find(m => m.year === parseInt(year) && m.month === parseInt(month));
                        setSelectedMonth(found);
                      }}
                    >
                      {availableMonths.map(m => (
                        <option key={`${m.year}-${m.month}`} value={`${m.year}-${m.month}`}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={handleDownloadReport}
                      disabled={reportLoading || !selectedMonth}
                      className="inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all font-medium text-sm disabled:opacity-50"
                    >
                      <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                      {reportLoading ? 'Generating...' : 'Download PDF'}
                    </button>
                    <button
                      onClick={handleSendReport}
                      disabled={sendingReport || !selectedMonth}
                      className="inline-flex items-center px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium text-sm disabled:opacity-50"
                    >
                      <EnvelopeIcon className="h-4 w-4 mr-2" />
                      {sendingReport ? 'Sending...' : 'Email Report'}
                    </button>
                  </div>
                </div>
              )}
              
              <div className="mt-6 p-5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <SparklesIcon className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 mb-2">Automatic Monthly Reports</p>
                    <p className="text-sm text-gray-600">We automatically send comprehensive reports to all farm members on the 1st of each month, including:</p>
                    <div className="mt-3 grid sm:grid-cols-2 gap-2">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
                        Revenue & expense trends
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
                        Egg production stats
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
                        Flock health tracking
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
                        AI-powered insights
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
